"use client"

import { Menu, Shield } from "lucide-react"

import { ConnectWalletButton } from "@shared/web3"
import { Button } from "@shared/ui/components/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@shared/ui/components/sheet"
import { cn } from "@shared/ui/lib/utils"

import { LocaleSwitcher } from "@/features/i18n/locale-switcher"
import { useTranslations } from "@/lib/i18n/client"
import { useWalletLabels } from "@/lib/wallet-labels"

const navLinks = [
  { href: "#score", key: "score" as const },
  { href: "#detection", key: "detection" as const },
  { href: "#threats", key: "threats" as const },
  { href: "#report", key: "report" as const },
  { href: "#network", key: "network" as const },
  { href: "#knowledge", key: "knowledge" as const },
]

export function SiteHeader() {
  const t = useTranslations("nav")
  const tCommon = useTranslations("common")
  const walletLabels = useWalletLabels()

  return (
    <header className="sticky top-0 z-50 border-b border-white/5 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4 md:h-16">
        <a href="#" className="flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-lg bg-sky-500/10 ring-1 ring-sky-500/30">
            <Shield className="size-4 text-sky-400" />
          </span>
          <span className="text-sm font-semibold tracking-tight md:text-base">
            {tCommon("appName")}
          </span>
        </a>

        <nav className="hidden items-center gap-1 lg:flex" aria-label={t("mainNav")}>
          {navLinks.map((link) => (
            <a
              key={link.key}
              href={link.href}
              className="rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t(link.key)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LocaleSwitcher />
          <div className="hidden sm:block">
            <ConnectWalletButton
              connectLabel={tCommon("connectWallet")}
              disconnectLabel={walletLabels.disconnect}
              labels={walletLabels}
              size="sm"
              className="bg-sky-500 hover:bg-sky-400"
            />
          </div>

          <Sheet>
            <SheetTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="lg:hidden"
                  aria-label={t("openMenu")}
                />
              }
            >
              <Menu className="size-5" />
            </SheetTrigger>
            <SheetContent side="right" className="border-white/10 bg-background/95 backdrop-blur-xl">
              <SheetHeader>
                <SheetTitle>{t("mobileNav")}</SheetTitle>
              </SheetHeader>
              <nav className={cn("mt-6 flex flex-col gap-1")} aria-label={t("mainNav")}>
                {navLinks.map((link) => (
                  <a
                    key={link.key}
                    href={link.href}
                    className="rounded-lg px-3 py-3 text-sm text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground"
                  >
                    {t(link.key)}
                  </a>
                ))}
              </nav>
              <div className="mt-6">
                <ConnectWalletButton
                  connectLabel={tCommon("connectWallet")}
                  disconnectLabel={walletLabels.disconnect}
                  labels={walletLabels}
                  className="w-full bg-sky-500 hover:bg-sky-400"
                />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
